import type { Parent, PhrasingContent, Root } from 'mdast';
import type { SourceHighlightMdastNode } from '#src/types/markdown.ts';

const OPEN_RX = /^<highlight\s+hashes="([^"]+)"\s*>$/i;
const CLOSE_RX = /^<\/highlight\s*>$/i;

function makeHighlightNode(hashes: string[], text: string): SourceHighlightMdastNode {
	return { type: 'sourceHighlight', hashes, text };
}

function processChildren(children: PhrasingContent[]): PhrasingContent[] {
	const result: PhrasingContent[] = [];
	let i = 0;

	while (i < children.length) {
		const child = children[i];

		if (child.type !== 'html') {
			result.push(child);
			i++;
			continue;
		}

		const openMatch = child.value.match(OPEN_RX);
		if (openMatch) {
			const hashes = openMatch[1].split(',').map((h) => h.trim());
			const textParts: string[] = [];
			let j = i + 1;
			let found = false;

			while (j < children.length) {
				const inner = children[j];
				if (inner.type === 'html' && CLOSE_RX.test(inner.value)) {
					result.push(makeHighlightNode(hashes, textParts.join('')));
					i = j + 1;
					found = true;
					break;
				}
				if (inner.type === 'text') {
					textParts.push(inner.value);
				}
				j++;
			}

			if (!found) {
				// No matching close tag — streaming/incomplete, skip to avoid raw HTML warning
				i++;
			}
			continue;
		}

		// Stray close tag (no matching open) — skip silently
		if (CLOSE_RX.test(child.value)) {
			i++;
			continue;
		}

		result.push(child);
		i++;
	}

	return result;
}

function visitParent(node: Parent): void {
	for (const child of node.children) {
		if ('children' in child) {
			visitParent(child as Parent);
		}
	}

	const anyChildren = node.children as PhrasingContent[];
	const hasHtml = anyChildren.some((c) => c.type === 'html');
	if (!hasHtml) {
		return;
	}
	(node as any).children = processChildren(anyChildren);
}

export function remarkSourceHighlights() {
	return (tree: Root) => {
		visitParent(tree as unknown as Parent);
	};
}
